<script lang="ts" setup>
  import { computed, onMounted, PropType, ref, toRefs } from "vue";

  import { Cache } from "frontend/utils/request-cache";
  import { Errors } from "frontend/uses/use-errors";
  import { StatusValue } from "frontend/utils/animation-status";
  import { FormSelectId } from "frontend/uses/use-form-select";
  import { SettingsProfile } from "frontend/interfaces/settings-profile";

  import FormSelect from "frontend/components/form/FormSelect.vue";
  import FormCheckboxGroup from "frontend/components/form/FormCheckboxGroup.vue";

  const props = defineProps({
    modelValue: {
      type: [String, Array] as PropType<FormSelectId | FormSelectId[]>,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    includeBlank: {
      type: [Boolean, String],
      default: false,
    },
    errors: {
      type: Array as PropType<Errors>,
      default: () => [],
    },
    animationStatus: {
      type: Number as PropType<StatusValue | null>,
      default: null,
    },
    visualSmall: {
      type: Boolean,
      default: false,
    },
  });

  const { modelValue } = toRefs(props);

  const emit = defineEmits(["update:model-value", "autosave"]);

  const settingsProfiles = ref<Array<SettingsProfile>>([]);
  const settingsProfilesLoaded = ref<boolean>(false);
  const options = computed(() =>
    settingsProfiles.value.map((entry) => ({
      id: entry.id,
      label: entry.name,
    }))
  );

  const onUpdateModelValue = (newValue: FormSelectId | null) => {
    emit("update:model-value", newValue);
  };
  const loadSettingsProfiles = async () => {
    settingsProfiles.value = await Cache.getCachedSettingsProfiles();
    settingsProfilesLoaded.value = true;
  };
  onMounted(loadSettingsProfiles);

  const multiple = computed(() => {
    return Array.isArray(modelValue.value);
  });

  // typescript type helper
  const modelValueSingle = computed(() => {
    // the condition should never be evaluated, because the corresponding template
    // will not be rendered
    return Array.isArray(modelValue.value) && modelValue.value.length > 0
      ? modelValue.value[0]
      : (modelValue.value as FormSelectId);
  });
  const modelValueMultiple = computed(() =>
    Array.isArray(modelValue.value)
      ? modelValue.value.filter((ele): ele is number | string => !!ele)
      : []
  );
</script>

<template>
  <div class="form-select-office-person">
    <template v-if="settingsProfilesLoaded">
      <template v-if="multiple">
        <FormCheckboxGroup
          v-bind:model-value="modelValueMultiple"
          v-bind:label="label"
          v-bind:options="options"
          v-bind:include-blank="includeBlank"
          v-bind:animation-status="animationStatus"
          v-bind:errors="errors"
          v-on:autosave="$emit('autosave')"
          v-on:update:model-value="onUpdateModelValue"
        />
      </template>
      <template v-else>
        <FormSelect
          v-bind:model-value="modelValueSingle"
          v-bind:label="label"
          v-bind:options="options"
          v-bind:include-blank="includeBlank"
          v-bind:errors="errors"
          v-bind:animation-status="animationStatus"
          v-bind:visual-small="visualSmall"
          v-on:autosave="$emit('autosave')"
          v-on:update:model-value="onUpdateModelValue"
        />
      </template>
    </template>
  </div>
</template>
